import React, {ReactElement, ReactNode} from 'react';

import cs from 'classnames';
import {withRouter, WithRouterProps} from 'react-router';
import styled from 'styled-components';

import {useCurrentUserState} from 'app/current-user/api';
import {PermsMap} from 'app/current-user/types';
import {getCurrentUserPerms} from 'app/current-user/utils';
import {ErrorContent} from 'app/general/loading/ErrorContent';
import {ErrorBoundary} from 'app/general/structures/ErrorBoundary';
import {FlexPanel} from 'app/general/structures/FlexPanel';

import {PageTitle} from './PageTitle';
import {SPBreadcrumbs} from './SPBreadcrumbs';

export interface Props extends WithRouterProps {
  horizontalPadding?: boolean;
  breadcrumbProps?: Record<string, unknown>;
  title?: string | null | ReactElement<any>;
  pageTitle?: string;
  pageActions?: ReactNode;
  subTitle?: ReactNode;
  actionArea?: ReactNode | ((perms: PermsMap) => ReactNode);
  pageActionAreaClassName?: string;
  children: ReactNode | ((perms: PermsMap) => ReactNode);
  sidebar?: ReactNode;
  versionbar?: ReactNode;
  readonly alternativeOverlayContent?: ReactNode;
}

export const SPPage = withRouter<Props>(
  ({
    children,
    title,
    subTitle,
    horizontalPadding = true,
    actionArea,
    pageActionAreaClassName,
    sidebar,
    versionbar,
    routes,
    params,
    breadcrumbProps = {},
    alternativeOverlayContent,
    pageActions,
    ...other
  }: Props) => {
    const permsMap = getCurrentUserPerms(useCurrentUserState().data!);

    function renderOrExecute(
      element: ReactNode | ((perms: PermsMap) => ReactNode)
    ) {
      return typeof element === 'function' ? element(permsMap) : element;
    }

    const pageTitle =
      other.pageTitle != null
        ? other.pageTitle
        : title != null
        ? title.toString()
        : null;

    const subTitleNode =
      subTitle != null ? (
        <small className="text-muted ml-auto">{subTitle}</small>
      ) : null;

    return (
      <FlexPanel
        type="vertical"
        scrollable={false}
        secondaryZindex="6"
        secondary={
          <StyledFixedContentDiv>
            <div className="d-flex flex-no-wrap justify-content-between align-items-center container-fluid">
              <ErrorBoundary name="breadcrumbs">
                <SPBreadcrumbs
                  routes={routes}
                  params={params}
                  breadcrumbProps={breadcrumbProps}
                />
              </ErrorBoundary>
              {actionArea != null ? (
                <div className="my-2 ml-auto action-area">
                  {renderOrExecute(actionArea)}
                </div>
              ) : null}
            </div>
          </StyledFixedContentDiv>
        }
      >
        <FlexPanel
          type="horizontal"
          secondaryZindex="5"
          secondary={<ErrorBoundary name="sidebar">{sidebar}</ErrorBoundary>}
        >
          <FlexPanel
            type="vertical"
            mainContentClass="scrollable-container"
            scrollable={true}
            secondaryZindex="4"
            secondary={
              <ErrorBoundary name="versionbar">{versionbar}</ErrorBoundary>
            }
          >
            {alternativeOverlayContent != null
              ? alternativeOverlayContent
              : null}
            <StyledContainerDiv
              className={cs('container-fluid', {
                'no-horizontal-padding': horizontalPadding !== true,
              })}
              hidden={alternativeOverlayContent != null}
            >
              {pageTitle != null ? <PageTitle title={pageTitle} /> : null}
              <div className="row">
                <div className="col-12">
                  {title != null || pageActions != null ? (
                    <div
                      className={cs(
                        'row mb-2 align-items-center',
                        pageActionAreaClassName
                      )}
                    >
                      {title != null ? (
                        <div className="col-12 col-sm">
                          <StyledTitleH1 marginBottom="0.5rem">
                            {title}
                            {subTitleNode}
                          </StyledTitleH1>
                        </div>
                      ) : null}
                      {pageActions != null ? (
                        <div className="col-auto ml-auto mt-3 mb-2">
                          {pageActions}
                        </div>
                      ) : null}
                    </div>
                  ) : null}

                  <ErrorBoundary ErrorComponent={ErrorContent} name="content">
                    {renderOrExecute(children)}
                  </ErrorBoundary>
                </div>
              </div>
            </StyledContainerDiv>
          </FlexPanel>
        </FlexPanel>
      </FlexPanel>
    );
  }
);

const StyledContainerDiv = styled.div`
  &,
  & > .row,
  & > .row > * {
    height: 100%;
  }

  &.no-horizontal-padding > .row > div {
    padding-left: 0;
    padding-right: 0;
  }
`;
export const StyledTitleH1 = styled.h1<{marginBottom?: string}>`
  display: flex;
  align-items: center;
  margin-top: 1rem;
  margin-bottom: ${({marginBottom}) =>
    marginBottom != null ? marginBottom : '1rem'};
  font-size: 1.25rem;

  > small {
    font-size: 0.8rem;
  }
`;
const StyledFixedContentDiv = styled.div`
  background: #fff;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  z-index: 9;

  .action-area {
    .btn {
      white-space: nowrap;
    }
  }
`;
